import React, { useState, useEffect, useRef } from 'react';
import { FaStar, FaChartLine, FaFistRaised, FaInfoCircle, FaRegUserCircle, FaUsers, FaCar, FaRoute } from 'react-icons/fa';
import { NavigationContainer } from './Templates';
import { supabase } from './data/supabaseClient';
import Spinner from './common/Spinner';
import { useTranslation } from 'react-i18next';
import './PostCardPagination.css';

/**
 * PostCardPagination component provides a 3-page pagination system for the middle content:
 * Page 0: Map and post details (default)
 * Page 1: User ratings and reviews 
 * Page 2: User stats and activity
 * 
 * Header (username/avatar) and footer (message button) stay fixed
 */
const PostCardPagination = ({ userId, username, post, headerContent, footerContent, children }) => {
  const { t } = useTranslation('common');
  const [currentPage, setCurrentPage] = useState(0);
  const [ratingData, setRatingData] = useState(null);
  const [statsData, setStatsData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedUserId, setLoadedUserId] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  
  // Touch handling for mobile swipe gestures
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchEndY, setTouchEndY] = useState(null);
  const minSwipeDistance = 50;
  const maxVerticalMovement = 30; // Allow some vertical movement during horizontal swipes

  // Reset to page 0 when user changes
  useEffect(() => {
    if (userId !== loadedUserId) {
      setCurrentPage(0);
    }
  }, [userId, loadedUserId]);

  // Fetch user data when navigating to rating/stats pages
  useEffect(() => {
    if (currentPage > 0 && userId && userId !== loadedUserId) {
      fetchUserData();
    }
  }, [currentPage, userId, loadedUserId]);

  const fetchUserData = async () => {
    if (!userId) return;
    
    try {
      setIsLoading(true);
      
      // Fetch both rating and stats data in parallel
      const [profileResult, reviewsResult, statsResult] = await Promise.allSettled([
        // Fetch profile data for scores
        supabase
          .from('profiles')
          .select('activity_score, overall_score')
          .eq('id', userId)
          .single(),
        
        // Fetch reviews data for average rating and count
        supabase
          .from('reviews')
          .select('rating')
          .eq('reviewee_id', userId),
        
        // Fetch public user stats
        supabase
          .rpc('get_public_user_stats', { target_user_id: userId })
      ]);
      
      // Process rating data
      if (profileResult.status === 'fulfilled' && reviewsResult.status === 'fulfilled') {
        const profileData = profileResult.value.data;
        const reviewsData = reviewsResult.value.data;
        
        if (!profileResult.value.error && !reviewsResult.value.error) {
          // Calculate average rating
          let averageRating = 0;
          let reviewCount = 0;
          
          if (reviewsData && reviewsData.length > 0) {
            const totalRating = reviewsData.reduce((sum, review) => sum + review.rating, 0);
            averageRating = totalRating / reviewsData.length;
            reviewCount = reviewsData.length;
          }
          
          const newRatingData = {
            reviewRating: averageRating,
            reviewCount: reviewCount,
            activityScore: parseFloat(profileData.activity_score) || 0,
            overallScore: parseFloat(profileData.overall_score) || 0,
            hasReviews: reviewCount > 0
          };
          
          setRatingData(newRatingData);
        }
      }
      
      // Process stats data
      if (statsResult.status === 'fulfilled') {
        const { value: { data: publicStatsResult, error: publicStatsError } } = statsResult;
          
        if (publicStatsError) {
          console.warn('Could not fetch public user stats:', publicStatsError.message);
          // Use fallback data
          const newStatsData = {
            totalPublished: 0,
            totalCompleted: 0,
            sharedDistance: 0,
            hasStatsData: false,
            hasPostsData: false
          };
          setStatsData(newStatsData);
        } else {
          // Use the data from the RPC function
          const statsResultData = Array.isArray(publicStatsResult) ? publicStatsResult[0] : publicStatsResult;
          
          const newStatsData = {
            totalPublished: statsResultData?.total_published || 0,
            totalCompleted: statsResultData?.total_completed || 0,
            sharedDistance: parseFloat(statsResultData?.shared_distance_km || 0),
            hasStatsData: statsResultData?.has_stats_data || false,
            hasPostsData: (statsResultData?.total_published || 0) > 0
          };
          
          setStatsData(newStatsData);
        }
      }
      
      setLoadedUserId(userId);
      
    } catch (err) {
      console.error('Error loading user data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Navigation functions
  const goToPage = (pageIndex) => {
    if (pageIndex === currentPage || isTransitioning) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentPage(pageIndex);
      setTimeout(() => setIsTransitioning(false), 100);
    }, 150);
  };

  const goToPrevPage = () => {
    if (currentPage > 0 && !isTransitioning) {
      goToPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < 2 && !isTransitioning) {
      goToPage(currentPage + 1);
    }
  };

  // Touch event handlers for swipe gestures - FIXED to allow vertical scrolling
  const onTouchStart = (e) => {
    // Only handle touches on the content area, not header/footer
    const target = e.target;
    const isHeaderOrFooter = target.closest('.pagination-header') || target.closest('.pagination-footer-fixed');
    
    if (isHeaderOrFooter) {
      // Don't interfere with header/footer touches - let them scroll naturally
      return;
    }
    
    setTouchEnd(null);
    setTouchEndY(null);
    setTouchStart(e.targetTouches[0].clientX);
    setTouchStartY(e.targetTouches[0].clientY);
  };

  const onTouchMove = (e) => {
    if (!touchStart || !touchStartY) return;
    
    setTouchEnd(e.targetTouches[0].clientX);
    setTouchEndY(e.targetTouches[0].clientY);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd || !touchStartY || !touchEndY) return;
    
    const horizontalDistance = touchStart - touchEnd;
    const verticalDistance = Math.abs(touchStartY - touchEndY);
    
    // Only trigger pagination if:
    // 1. Horizontal movement is significant
    // 2. Vertical movement is minimal (not a scroll gesture)
    const isHorizontalSwipe = Math.abs(horizontalDistance) > minSwipeDistance;
    const isVerticalScroll = verticalDistance > maxVerticalMovement;
    
    if (isHorizontalSwipe && !isVerticalScroll && !isTransitioning) {
      const isLeftSwipe = horizontalDistance > 0;
      const isRightSwipe = horizontalDistance < 0;
      
      if (isLeftSwipe && currentPage < 2) {
        goToNextPage();
      } else if (isRightSwipe && currentPage > 0) {
        goToPrevPage();
      }
    }
    
    // Reset touch state
    setTouchStart(null);
    setTouchEnd(null);
    setTouchStartY(null);
    setTouchEndY(null);
  };

  // Render stars for ratings
  const renderStars = (rating, size = 'small') => {
    return [1, 2, 3, 4, 5].map((value) => (
      <FaStar 
        key={value}
        className={`pagination-star ${size} ${value <= Math.round(rating) ? 'filled' : 'empty'}`}
      />
    ));
  };

  // Page 1: User Ratings Content
  const renderRatingsPage = () => {
    if (isLoading || (userId !== loadedUserId)) {
      return (
        <div className="pagination-loading">
          <Spinner
            size="small"
            showText={true}
            customText={t('user_stats.loading_ratings')}
          />
        </div>
      );
    }

    if (!ratingData) {
      return (
        <div className="pagination-no-data">
          <span>{t('user_stats.rating_data_unavailable')}</span>
        </div>
      );
    }

    return (
      <div className="pagination-content ratings-content">
        {/* User Reviews */}
        <div className="rating-row main-rating">
          <div className="rating-label">
            <FaRegUserCircle className="rating-icon" />
            <span className="label-text">{t('user_stats.reviews')}</span>
            <span className="rating-value">
              {ratingData.hasReviews ? ratingData.reviewRating.toFixed(1) : 'N/A'}
            </span>
          </div>
          <div className="star-container">
            {ratingData.hasReviews ? (
              renderStars(ratingData.reviewRating)
            ) : (
              <span className="no-reviews">{t('user_stats.no_reviews')}</span>
            )}
          </div>
        </div>

        {/* Activity Score */}
        <div className="rating-row">
          <div className="rating-label">
            <FaChartLine className="rating-icon" />
            <span className="label-text">{t('user_stats.activity')}</span>
            <span className="rating-value">{ratingData.activityScore.toFixed(1)}</span>
          </div>
          <div className="star-container">
            {renderStars(ratingData.activityScore)}
          </div>
        </div>

        {/* Overall Score */}
        <div className="rating-row">
          <div className="rating-label">
            <FaFistRaised className="rating-icon" />
            <span className="label-text">{t('user_stats.overall')}</span>
            <span className="rating-value">{ratingData.overallScore.toFixed(1)}</span>
          </div>
          <div className="star-container">
            {renderStars(ratingData.overallScore)}
          </div>
        </div>

        {/* Review Count */}
        <div className="pagination-footer">
          <FaInfoCircle className="info-icon" />
          <span className="review-count">
            {ratingData.reviewCount === 1
              ? t('user_stats.based_on_reviews', { count: ratingData.reviewCount })
              : t('user_stats.based_on_reviews_plural', { count: ratingData.reviewCount })
            }
          </span>
        </div>
      </div>
    );
  };

  // Page 2: User Stats Content
  const renderStatsPage = () => {
    if (isLoading || (userId !== loadedUserId)) {
      return (
        <div className="pagination-loading">
          <Spinner
            size="small"
            showText={true}
            customText={t('user_stats.loading_stats')}
          />
        </div>
      );
    }

    if (!statsData) {
      return (
        <div className="pagination-no-data">
          <span>{t('user_stats.stats_data_unavailable')}</span>
        </div>
      );
    }

    return (
      <div className="pagination-content stats-content">
        {/* Total Posts Published */}
        <div className="rating-row main-rating">
          <div className="rating-label">
            <FaUsers className="rating-icon" />
            <span className="label-text">{t('user_stats.published')}</span>
            <span className="rating-value">{statsData.totalPublished || 0}</span>
          </div>
          <div className="star-container">
            <span className="stats-detail">{t('user_stats.posts_and_packages')}</span>
          </div>
        </div>

        {/* Total Completed */}
        <div className="rating-row">
          <div className="rating-label">
            <FaCar className="rating-icon" />
            <span className="label-text">{t('user_stats.completed')}</span>
            <span className={`rating-value ${!statsData.hasStatsData ? 'private-stat' : ''}`}>
              {statsData.hasStatsData ? (statsData.totalCompleted || 0) : t('user_stats.private')}
            </span>
          </div>
          <div className="star-container">
            <span className={`stats-detail ${!statsData.hasStatsData ? 'private-detail' : ''}`}>
              {statsData.hasStatsData ? t('user_stats.rides_and_deliveries') : t('user_stats.not_visible')}
            </span>
          </div>
        </div>

        {/* Shared Distance */}
        <div className="rating-row">
          <div className="rating-label">
            <FaRoute className="rating-icon" />
            <span className="label-text">{t('user_stats.shared')}</span>
            <span className={`rating-value ${!statsData.hasStatsData ? 'private-stat' : ''}`}>
              {statsData.hasStatsData ? `${(statsData.sharedDistance || 0).toFixed(0)} km` : t('user_stats.private')}
            </span>
          </div>
          <div className="star-container">
            <span className={`stats-detail ${!statsData.hasStatsData ? 'private-detail' : ''}`}>
              {statsData.hasStatsData ? t('user_stats.distance') : t('user_stats.not_visible')}
            </span>
          </div>
        </div>

        {/* Stats Footer */}
        <div className="pagination-footer">
          <FaInfoCircle className="info-icon" />
          <span className="review-count">
            {statsData.hasStatsData ?
              t('user_stats.user_activity_statistics') :
              t('user_stats.some_stats_private')}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div 
      ref={containerRef}
      className="post-card-pagination"
    >
      {/* Fixed Header - Username and Avatar */}
      <div className="pagination-header">
        {headerContent}
      </div>

      {/* Navigation Controls */}
      <div className="pagination-navigation">
        <NavigationContainer
          currentPage={currentPage}
          totalPages={3}
          onPrevClick={goToPrevPage}
          onNextClick={goToNextPage}
          disabled={isTransitioning}
          compact={true}
          showDots={true}
          className="post-card-nav"
        />
      </div>

      {/* Sliding Middle Content */}
      <div className="pagination-content-container">
        <div 
        className={`pagination-content-slider ${isTransitioning ? 'transitioning' : ''}`}
        style={{
        transform: `translateX(-${currentPage * 33.333}%)`,
        transition: isTransitioning ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none'
        }}
          onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
          {/* Page 0: Default Content (Map + Post Details) */}
          <div className="pagination-content-page">
            {children}
          </div>
          
          {/* Page 1: User Ratings */}
          <div className="pagination-content-page">
            {renderRatingsPage()}
          </div>
          
          {/* Page 2: User Stats */}
          <div className="pagination-content-page">
            {renderStatsPage()}
          </div>
        </div>
      </div>

      {/* Fixed Footer - Message Button */}
      <div className="pagination-footer-fixed">
        {footerContent}
      </div>
    </div>
  );
};

export default PostCardPagination;